/**
 * @career/cv-render-v2 — the ONE CV rendering logic shared by the api export
 * routes (Markdown, JSON-LD) and the SPA print view (`/cv-print`). Pure, zero
 * runtime deps: it takes a structural `Cv` (type-only import from
 * `@career/definition-v2`, erased at build — no arktype in the browser bundle) and
 * produces a locale-tagged render model + document renderers.
 *
 * Section ordering, visibility, and titles are single-sourced here so MD, JSON-LD,
 * and the print DOM never drift (app-architecture.md §5). Content is German
 * (de-DE, single-locale v1 — i18n-adr.md I1); structural ids stay English.
 */

import type { Cv } from "@career/definition-v2"

// ─── Render model (the shared, presentation-agnostic view) ───────────────────

export interface CvRenderEntry {
  title: string
  subtitle?: string
  /** A dates / place line. */
  meta?: string
  /** Summary + highlight/keyword/course lines. */
  lines: string[]
  url?: string
  /** Evidence-ledger claim URNs backing this entry (per-entry provenance). */
  claimRefs: string[]
}

export interface CvRenderSection {
  /** English structural id (`work`, `education`, …) — for keys/anchors. */
  id: string
  /** German section heading (content). */
  title: string
  entries: CvRenderEntry[]
}

export interface CvRenderContact {
  email?: string
  phone?: string
  url?: string
  location?: string
  profiles: { network: string; url?: string }[]
}

export interface CvRenderModel {
  name: string
  label?: string
  summary?: string
  /** Portrait object-store key, if set. */
  image?: string
  contact: CvRenderContact
  sections: CvRenderSection[]
  version: number
  locale: string
}

type SectionId =
  | "work" | "education" | "projects" | "skills" | "languages"
  | "certificates" | "awards" | "publications" | "volunteer" | "interests" | "references"

const DEFAULT_ORDER: SectionId[] = [
  "work", "education", "projects", "skills", "languages",
  "certificates", "awards", "publications", "volunteer", "interests", "references",
]

const DEFAULT_TITLE: Record<SectionId, string> = {
  work: "Berufserfahrung",
  education: "Ausbildung",
  projects: "Projekte",
  skills: "Fähigkeiten",
  languages: "Sprachen",
  certificates: "Zertifikate",
  awards: "Auszeichnungen",
  publications: "Publikationen",
  volunteer: "Ehrenamt",
  interests: "Interessen",
  references: "Referenzen",
}

const nonEmpty = (v: unknown): v is string => typeof v === "string" && v.trim().length > 0
const refs = (v: readonly string[] | undefined): string[] => (Array.isArray(v) ? [...v] : [])

/** `startDate – endDate` (blank end → "heute"); omitted entirely when neither is set. */
function dateRange(start?: string, end?: string): string | undefined {
  if (!nonEmpty(start) && !nonEmpty(end)) return undefined
  const from = nonEmpty(start) ? start : "?"
  const to = nonEmpty(end) ? end : "heute"
  return `${from} – ${to}`
}

function locationLine(loc: Cv["basics"]["location"]): string | undefined {
  if (!loc) return undefined
  const parts = [loc.city, loc.region, loc.countryCode].filter(nonEmpty)
  return parts.length > 0 ? parts.join(", ") : undefined
}

function sectionEntries(cv: Cv, id: SectionId): CvRenderEntry[] {
  switch (id) {
    case "work":
      return cv.work.map((w) => ({
        title: w.name,
        ...(nonEmpty(w.position) ? { subtitle: w.position } : {}),
        ...(dateRange(w.startDate, w.endDate) ? { meta: dateRange(w.startDate, w.endDate) } : {}),
        lines: [...(nonEmpty(w.summary) ? [w.summary] : []), ...w.highlights.filter(nonEmpty)],
        ...(nonEmpty(w.url) ? { url: w.url } : {}),
        claimRefs: refs(w.evidenceClaims),
      }))
    case "volunteer":
      return cv.volunteer.map((v) => ({
        title: v.organization,
        ...(nonEmpty(v.position) ? { subtitle: v.position } : {}),
        ...(dateRange(v.startDate, v.endDate) ? { meta: dateRange(v.startDate, v.endDate) } : {}),
        lines: [...(nonEmpty(v.summary) ? [v.summary] : []), ...v.highlights.filter(nonEmpty)],
        ...(nonEmpty(v.url) ? { url: v.url } : {}),
        claimRefs: refs(v.evidenceClaims),
      }))
    case "education":
      return cv.education.map((e) => ({
        title: e.institution,
        ...(nonEmpty(e.studyType) || nonEmpty(e.area)
          ? { subtitle: [e.studyType, e.area].filter(nonEmpty).join(" · ") }
          : {}),
        ...(dateRange(e.startDate, e.endDate) ? { meta: dateRange(e.startDate, e.endDate) } : {}),
        lines: [...(nonEmpty(e.score) ? [`Note: ${e.score}`] : []), ...e.courses.filter(nonEmpty)],
        ...(nonEmpty(e.url) ? { url: e.url } : {}),
        claimRefs: refs(e.evidenceClaims),
      }))
    case "projects":
      return cv.projects.map((p) => ({
        title: p.name,
        ...(p.roles.filter(nonEmpty).length > 0 ? { subtitle: p.roles.filter(nonEmpty).join(", ") } : {}),
        ...(dateRange(p.startDate, p.endDate) ? { meta: dateRange(p.startDate, p.endDate) } : {}),
        lines: [...(nonEmpty(p.description) ? [p.description] : []), ...p.highlights.filter(nonEmpty)],
        ...(nonEmpty(p.url) ? { url: p.url } : {}),
        claimRefs: refs(p.evidenceClaims),
      }))
    case "skills":
      return cv.skills.map((s) => ({
        title: s.name,
        ...(nonEmpty(s.level) ? { meta: s.level } : {}),
        lines: s.keywords.filter(nonEmpty),
        claimRefs: refs(s.evidenceClaims),
      }))
    case "languages":
      return cv.languages.map((l) => ({
        title: l.language,
        ...(nonEmpty(l.fluency) ? { meta: l.fluency } : {}),
        lines: [],
        claimRefs: refs(l.evidenceClaims),
      }))
    case "certificates":
      return cv.certificates.map((c) => ({
        title: c.name,
        ...(nonEmpty(c.issuer) ? { subtitle: c.issuer } : {}),
        ...(nonEmpty(c.date) ? { meta: c.date } : {}),
        lines: [],
        ...(nonEmpty(c.url) ? { url: c.url } : {}),
        claimRefs: refs(c.evidenceClaims),
      }))
    case "awards":
      return cv.awards.map((a) => ({
        title: a.title,
        ...(nonEmpty(a.awarder) ? { subtitle: a.awarder } : {}),
        ...(nonEmpty(a.date) ? { meta: a.date } : {}),
        lines: nonEmpty(a.summary) ? [a.summary] : [],
        claimRefs: refs(a.evidenceClaims),
      }))
    case "publications":
      return cv.publications.map((p) => ({
        title: p.name,
        ...(nonEmpty(p.publisher) ? { subtitle: p.publisher } : {}),
        ...(nonEmpty(p.releaseDate) ? { meta: p.releaseDate } : {}),
        lines: nonEmpty(p.summary) ? [p.summary] : [],
        ...(nonEmpty(p.url) ? { url: p.url } : {}),
        claimRefs: refs(p.evidenceClaims),
      }))
    case "interests":
      return cv.interests.map((i) => ({ title: i.name, lines: i.keywords.filter(nonEmpty), claimRefs: [] }))
    case "references":
      return cv.references.map((r) => ({
        title: r.name,
        lines: nonEmpty(r.reference) ? [r.reference] : [],
        claimRefs: refs(r.evidenceClaims),
      }))
  }
}

/** Build the ordered, visibility-filtered, title-resolved render model. */
export function buildCvRenderModel(cv: Cv): CvRenderModel {
  const meta = cv.sectionMeta ?? {}
  const sections: CvRenderSection[] = DEFAULT_ORDER.map((id) => {
    const m = (meta as Record<string, { title?: string; visible?: boolean; order?: number } | undefined>)[id]
    return {
      id,
      order: m?.order ?? DEFAULT_ORDER.indexOf(id),
      visible: m?.visible ?? true,
      title: nonEmpty(m?.title) ? m!.title! : DEFAULT_TITLE[id],
      entries: sectionEntries(cv, id),
    }
  })
    .filter((s) => s.visible && s.entries.length > 0)
    .sort((a, b) => a.order - b.order)
    .map(({ id, title, entries }) => ({ id, title, entries }))

  const { basics } = cv
  return {
    name: basics.name,
    ...(nonEmpty(basics.label) ? { label: basics.label } : {}),
    ...(nonEmpty(basics.summary) ? { summary: basics.summary } : {}),
    ...(basics.image ? { image: basics.image.key } : {}),
    contact: {
      ...(nonEmpty(basics.email) ? { email: basics.email } : {}),
      ...(nonEmpty(basics.phone) ? { phone: basics.phone } : {}),
      ...(nonEmpty(basics.url) ? { url: basics.url } : {}),
      ...(locationLine(basics.location) ? { location: locationLine(basics.location) } : {}),
      profiles: basics.profiles.map((p) => ({ network: p.network, ...(nonEmpty(p.url) ? { url: p.url } : {}) })),
    },
    sections,
    version: cv.version,
    locale: cv.locale,
  }
}

// ─── Markdown renderer (clean human-readable document) ───────────────────────

/** Render a fixed CV version as a clean Markdown document. */
export function renderCvMarkdown(cv: Cv): string {
  const model = buildCvRenderModel(cv)
  const out: string[] = [`# ${model.name}`]
  if (model.label) out.push(`*${model.label}*`)

  const contact: string[] = []
  if (model.contact.email) contact.push(model.contact.email)
  if (model.contact.phone) contact.push(model.contact.phone)
  if (model.contact.location) contact.push(model.contact.location)
  if (model.contact.url) contact.push(model.contact.url)
  for (const p of model.contact.profiles) contact.push(p.url ? `${p.network}: ${p.url}` : p.network)
  if (contact.length > 0) out.push("", contact.join(" · "))

  if (model.summary) out.push("", model.summary)

  for (const section of model.sections) {
    out.push("", `## ${section.title}`)
    for (const e of section.entries) {
      const head = [e.title, e.subtitle].filter(nonEmpty).join(" — ")
      out.push("", e.meta ? `### ${head}  \n_${e.meta}_` : `### ${head}`)
      for (const line of e.lines) out.push(`- ${line}`)
      if (e.url) out.push(`- ${e.url}`)
    }
  }
  return out.join("\n") + "\n"
}

// ─── JSON-LD renderer (schema.org Person) ────────────────────────────────────

/** Render a fixed CV version as a schema.org `Person` JSON-LD object. */
export function renderCvJsonLd(cv: Cv): Record<string, unknown> {
  const { basics } = cv
  const person: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: basics.name,
  }
  if (nonEmpty(basics.label)) person.jobTitle = basics.label
  if (nonEmpty(basics.summary)) person.description = basics.summary
  if (nonEmpty(basics.email)) person.email = basics.email
  if (nonEmpty(basics.phone)) person.telephone = basics.phone
  if (nonEmpty(basics.url)) person.url = basics.url
  if (basics.location) {
    const address: Record<string, unknown> = { "@type": "PostalAddress" }
    if (nonEmpty(basics.location.city)) address.addressLocality = basics.location.city
    if (nonEmpty(basics.location.region)) address.addressRegion = basics.location.region
    if (nonEmpty(basics.location.postalCode)) address.postalCode = basics.location.postalCode
    if (nonEmpty(basics.location.countryCode)) address.addressCountry = basics.location.countryCode
    if (Object.keys(address).length > 1) person.address = address
  }
  const sameAs = basics.profiles.map((p) => p.url).filter(nonEmpty)
  if (sameAs.length > 0) person.sameAs = sameAs
  if (cv.work.length > 0) {
    person.hasOccupation = cv.work.map((w) => ({
      "@type": "Occupation",
      name: nonEmpty(w.position) ? w.position : w.name,
      ...(nonEmpty(w.name) ? { hiringOrganization: { "@type": "Organization", name: w.name } } : {}),
    }))
    const latest = cv.work[0]!
    person.worksFor = { "@type": "Organization", name: latest.name }
  }
  if (cv.education.length > 0) {
    person.alumniOf = cv.education.map((e) => ({
      "@type": "EducationalOrganization",
      name: e.institution,
      ...(nonEmpty(e.area) ? { department: e.area } : {}),
    }))
  }
  if (cv.certificates.length > 0) {
    person.hasCredential = cv.certificates.map((c) => ({
      "@type": "EducationalOccupationalCredential",
      name: c.name,
      ...(nonEmpty(c.issuer) ? { recognizedBy: { "@type": "Organization", name: c.issuer } } : {}),
    }))
  }
  const knowsAbout = cv.skills.map((s) => s.name).filter(nonEmpty)
  if (knowsAbout.length > 0) person.knowsAbout = knowsAbout
  const languages = cv.languages.map((l) => l.language).filter(nonEmpty)
  if (languages.length > 0) person.knowsLanguage = languages

  // An explicit `jsonLd` override on the Cv wins field-by-field (author intent).
  return cv.jsonLd ? { ...person, ...cv.jsonLd } : person
}
